import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Coins, Banknote, Calculator, AlertTriangle, CheckCircle2, 
  XCircle, RefreshCw, Clock, User, FileText, TrendingUp, TrendingDown,
  CreditCard, Smartphone, Shield, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// FCFA Denominations for display
const COINS = [25, 50, 100, 200];
const BILLS = [500, 1000, 2000, 5000, 10000];
const TOLERANCE_THRESHOLD = 100;
const LARGE_VARIANCE_THRESHOLD = 5000;

const CashClosingValidationModal = ({ 
  open, 
  onClose, 
  closing,
  api,
  onValidationComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [validatorNotes, setValidatorNotes] = useState('');
  
  const formatCFA = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleValidate = async (approved, requestRecount = false) => {
    setLoading(true);
    try {
      await api().post('/cash-closing/validate', {
        closing_id: closing.id,
        approved,
        validator_notes: validatorNotes,
        request_recount: requestRecount
      });
      
      if (requestRecount) {
        toast.success('Recomptage demandé');
      } else if (approved) {
        toast.success('Clôture validée avec succès');
      } else {
        toast.success('Clôture rejetée');
      }
      
      onValidationComplete && onValidationComplete();
      onClose();
    } catch (error) {
      console.error('Error validating closing:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };
  
  if (!closing) return null;
  
  const variance = closing.variance || 0;
  const varianceAbs = Math.abs(variance);
  const requiresReason = varianceAbs > TOLERANCE_THRESHOLD;
  const canValidate = !requiresReason || closing.variance_reason;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Validation de Clôture - {closing.register_name}
          </DialogTitle>
          <DialogDescription>
            Vérifiez les informations et validez ou rejetez la clôture
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4 py-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <Badge className={cn(
                closing.status === 'pending' && "bg-yellow-500",
                closing.status === 'recount_requested' && "bg-orange-500",
                closing.status === 'validated' && "bg-green-500",
                closing.status === 'rejected' && "bg-red-500"
              )}>
                {closing.status === 'pending' && 'En attente de validation'}
                {closing.status === 'recount_requested' && 'Recomptage demandé'}
                {closing.status === 'validated' && 'Validée'}
                {closing.status === 'rejected' && 'Rejetée'}
              </Badge>
              
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(closing.created_at)}
              </span>
            </div>
            
            {/* Created By */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Créée par:</span>
                  <span className="font-bold">{closing.created_by_name}</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Financial Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Récapitulatif Financier</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fond de caisse:</span>
                      <span className="font-bold">{formatCFA(closing.starting_cash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ventes espèces:</span>
                      <span className="font-bold text-green-600">+{formatCFA(closing.cash_sales)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="font-bold">Théorique:</span>
                      <span className="font-bold text-blue-600">{formatCFA(closing.theoretical_cash)}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mobile Money:</span>
                      <span className="font-medium">{formatCFA(closing.mobile_money_sales)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Carte:</span>
                      <span className="font-medium">{formatCFA(closing.card_sales)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total ventes:</span>
                      <span className="font-bold">{formatCFA(closing.total_sales)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Counting Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Détail du Comptage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Coins */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Coins className="w-3 h-3" /> Pièces
                    </p>
                    <div className="space-y-1">
                      {COINS.map(d => {
                        const qty = closing.coins_detail?.[d] || 0;
                        if (qty === 0) return null;
                        return (
                          <div key={d} className="flex justify-between text-sm">
                            <span>{formatCFA(d)} × {qty}</span>
                            <span className="font-medium">{formatCFA(d * qty)}</span>
                          </div>
                        );
                      })}
                      <div className="flex justify-between text-sm font-bold pt-1 border-t">
                        <span>Total pièces:</span>
                        <span className="text-amber-600">{formatCFA(closing.coins_total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bills */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Banknote className="w-3 h-3" /> Billets
                    </p>
                    <div className="space-y-1">
                      {BILLS.map(d => {
                        const qty = closing.bills_detail?.[d] || 0;
                        if (qty === 0) return null;
                        return (
                          <div key={d} className="flex justify-between text-sm">
                            <span>{formatCFA(d)} × {qty}</span>
                            <span className="font-medium">{formatCFA(d * qty)}</span>
                          </div>
                        );
                      })}
                      <div className="flex justify-between text-sm font-bold pt-1 border-t">
                        <span>Total billets:</span>
                        <span className="text-green-600">{formatCFA(closing.bills_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Variance */}
            <Card className={cn(
              "border-2",
              varianceAbs === 0 && "border-green-400 bg-green-50/50",
              varianceAbs > 0 && varianceAbs <= TOLERANCE_THRESHOLD && "border-yellow-400 bg-yellow-50/50",
              varianceAbs > TOLERANCE_THRESHOLD && varianceAbs < LARGE_VARIANCE_THRESHOLD && "border-orange-400 bg-orange-50/50",
              varianceAbs >= LARGE_VARIANCE_THRESHOLD && "border-red-400 bg-red-50/50"
            )}>
              <CardContent className="pt-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Compté</p>
                    <p className="text-xl font-black">{formatCFA(closing.counted_total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Théorique</p>
                    <p className="text-xl font-black text-blue-600">{formatCFA(closing.theoretical_cash)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Écart</p>
                    <p className={cn(
                      "text-xl font-black flex items-center justify-center gap-1",
                      variance === 0 && "text-green-600",
                      variance > 0 && "text-blue-600",
                      variance < 0 && "text-red-600"
                    )}>
                      {variance > 0 ? <TrendingUp className="w-4 h-4" /> : variance < 0 ? <TrendingDown className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      {variance >= 0 ? '+' : ''}{formatCFA(variance)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Variance Reason */}
            {closing.variance_reason && (
              <Card className="border-orange-200 bg-orange-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="w-4 h-4" />
                    Motif de l'écart
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{closing.variance_reason}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Recount History */}
            {closing.recounts && closing.recounts.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Historique des recomptages ({closing.recounts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {closing.recounts.map((recount, idx) => (
                    <div key={idx} className="p-2 bg-muted/30 rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span>Recomptage #{recount.recount_number}</span>
                        <span className="text-muted-foreground">{formatDate(recount.counted_at)}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>Par: {recount.counted_by_name}</span>
                        <span className={cn(
                          "font-bold",
                          recount.variance === 0 && "text-green-600",
                          recount.variance !== 0 && "text-orange-600"
                        )}>
                          Écart: {recount.variance >= 0 ? '+' : ''}{formatCFA(recount.variance)}
                        </span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            {/* Validator Notes */}
            {closing.status === 'pending' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Notes du validateur</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={validatorNotes}
                    onChange={(e) => setValidatorNotes(e.target.value)}
                    placeholder="Ajoutez des notes de validation si nécessaire..."
                    className="min-h-[60px]"
                  />
                </CardContent>
              </Card>
            )}
            
            {/* Warning if reason missing */}
            {requiresReason && !closing.variance_reason && (
              <Card className="border-red-300 bg-red-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">
                      Motif manquant - Un motif est requis pour valider cette clôture
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
        
        {/* Actions */}
        {closing.status === 'pending' && (
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            
            <div className="flex gap-2">
              {varianceAbs >= LARGE_VARIANCE_THRESHOLD && (
                <Button
                  variant="outline"
                  onClick={() => handleValidate(false, true)}
                  disabled={loading}
                  className="border-orange-400 text-orange-600 hover:bg-orange-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Demander recomptage
                </Button>
              )}
              
              <Button
                variant="destructive"
                onClick={() => handleValidate(false)}
                disabled={loading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter
              </Button>
              
              <Button
                onClick={() => handleValidate(true)}
                disabled={loading || !canValidate}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Valider
              </Button>
            </div>
          </div>
        )}
        
        {closing.status !== 'pending' && (
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose}>Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CashClosingValidationModal;
